import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import axios from 'axios';
import {
    Box, Button, CircularProgress, Dialog, DialogContent, DialogTitle,
    TextField, Typography, IconButton, Tooltip,
} from '@mui/material';
import { ChevronLeft, ChevronRight, FileText, Lock, MailCheck, AlertCircle, Clock, CreditCard, X, CheckCircle, Download } from 'lucide-react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import configJson from '../api/config.json';
import SharedReportView from '../components/shared-report-view';
import { ReportSnapshot } from '../util/report-snapshot';
import { ViewerShell, CenteredCard } from '../components/viewer-shell';
import SharedFolderBrowser from './shared-folder-browser';

// ─── PDF worker ────────────────────────────────────────────────────────────────
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// ─── Helpers ───────────────────────────────────────────────────────────────────

type Env = 'development' | 'production';
const config = configJson as Record<Env, { protocol: string; domain: string; port: number }>;

const getApiBase = (): string => {
    const env = (process.env.REACT_APP_ENV || 'production') as Env;
    if (env === 'development') {
        const { protocol, domain, port } = config[env];
        const isDefault = (protocol === 'http' && port === 80) || (protocol === 'https' && port === 443);
        return isDefault ? `${protocol}://${domain}` : `${protocol}://${domain}:${port}`;
    }
    const { protocol, domain } = config.production;
    return `${protocol}://${domain}`;
};

const API_BASE = getApiBase();


const tokenKey = (slug: string) => `doc_token_${slug}`;

const getSessionId = (): string => {
    const key = 'doc_session_id';
    let id = sessionStorage.getItem(key);
    if (!id) {
        id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
        sessionStorage.setItem(key, id);
    }
    return id;
};

// ─── Types ─────────────────────────────────────────────────────────────────────

type ViewState =
    | 'loading'
    | 'not_found'
    | 'expired'
    | 'access_required'
    | 'password_required'
    | 'email_sent'
    | 'access_error'
    | 'viewing';

interface AccessInfo {
    title: string;
    type: 'pdf' | 'webpage';
    webpageUrl: string | null;
    accessType: 'anonymous' | 'email_list';
    isExpired: boolean;
    allowDownloads: boolean;
    watermarkTemplate: string | null;
    watermarkViewer: { email: string | null; ip: string | null };
    canPay: boolean;
    stripePublishableKey: string | null;
    invoiceAmount: number | null;
    invoiceCurrency: string | null;
}

// ─── Component ─────────────────────────────────────────────────────────────────

const DocumentViewer: React.FC = () => {
    const { slug } = useParams<{ slug: string }>();
    const [searchParams, setSearchParams] = useSearchParams();

    const [viewState, setViewState] = useState<ViewState>('loading');
    const [accessInfo, setAccessInfo] = useState<AccessInfo | null>(null);
    const [accessToken, setAccessToken] = useState<string | null>(null);
    const [errorMsg, setErrorMsg] = useState('');
    // This slug turned out to belong to a shared folder, not a document -
    // delegate entirely to the dedicated folder-browsing viewer.
    const [folderMode, setFolderMode] = useState(false);

    // PDF state
    const [numPages, setNumPages] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [pdfWidth, setPdfWidth] = useState(800);
    const containerRef = useRef<HTMLDivElement>(null);

    // Webpage (shared report snapshot) state
    const [snapshot, setSnapshot] = useState<ReportSnapshot | null>(null);
    const [snapshotError, setSnapshotError] = useState('');

    // Email request form
    const [email, setEmail] = useState('');
    // Password for a protected link: the typed value, the unlock token (restricted links
    // keep it for the verification request), and whether a check is in progress.
    const [passwordInput, setPasswordInput] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [unlocking, setUnlocking] = useState(false);
    const [unlockToken, setUnlockToken] = useState<string | null>(null);
    const [requestingAccess, setRequestingAccess] = useState(false);
    const [emailError, setEmailError] = useState('');

    // Stripe payment
    const [payModalOpen, setPayModalOpen] = useState(false);
    const [payClientSecret, setPayClientSecret] = useState<string | null>(null);
    const [payLoading, setPayLoading] = useState(false);
    const [payError, setPayError] = useState('');
    const [paySuccess, setPaySuccess] = useState(false);

    // Tracking
    const sessionId = useRef(getSessionId());
    const trackIdRef = useRef<number | null>(null);
    const currentPageRef = useRef(1);

    // ─── Resolve access token on mount ──────────────────────────────────────

    useEffect(() => {
        if (!slug) return;

        // Capture ?accessToken from URL (set by backend verify redirect)
        const urlToken = searchParams.get('accessToken');
        const urlError = searchParams.get('error');

        if (urlToken) {
            sessionStorage.setItem(tokenKey(slug), urlToken);
            setAccessToken(urlToken);
            // Clean the token out of the URL
            setSearchParams({}, { replace: true });
        } else {
            const stored = sessionStorage.getItem(tokenKey(slug));
            if (stored) setAccessToken(stored);
        }

        if (urlError) {
            const messages: Record<string, string> = {
                invalid_token: 'This verification link is invalid or has already been used.',
                expired: 'This document link has expired.',
                access_revoked: 'Your access to this document has been revoked.',
                missing_params: 'The verification link is missing required parameters.',
                document_not_found: 'Document not found.',
                server_error: 'Something went wrong. Please try again.',
            };
            setErrorMsg(messages[urlError] || 'An error occurred.');
            setViewState('access_error');
            setSearchParams({}, { replace: true });
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // ─── Fetch document access info ──────────────────────────────────────────

    useEffect(() => {
        if (!slug || viewState === 'access_error') return;

        const token = accessToken || sessionStorage.getItem(tokenKey(slug));
        const headers: Record<string, string> = {};
        if (token) headers['Authorization'] = `Bearer ${token}`;

        axios.get(`${API_BASE}/documents/${slug}/access`, { headers })
            .then(({ data }) => {
                const info: AccessInfo = {
                    title: data.title,
                    type: data.type,
                    webpageUrl: data.webpageUrl ?? null,
                    accessType: data.accessType,
                    isExpired: data.isExpired,
                    allowDownloads: data.allowDownloads !== false,
                    canPay: !!data.canPay,
                    stripePublishableKey: data.stripePublishableKey || null,
                    invoiceAmount: data.invoiceAmount ?? null,
                    invoiceCurrency: data.invoiceCurrency ?? null,
                    watermarkTemplate: data.watermarkTemplate ?? null,
                    watermarkViewer: data.watermarkViewer ?? { email: null, ip: null },
                };
                setAccessInfo(info);

                if (!data.success || data.isExpired) {
                    setViewState(data.isExpired ? 'expired' : 'not_found');
                    return;
                }

                // Password-protected link: the password comes before anything else.
                if (data.requiresPassword && !(accessToken || sessionStorage.getItem(tokenKey(slug)))) {
                    setViewState('password_required');
                    return;
                }

                if (info.accessType === 'anonymous') {
                    setViewState('viewing');
                    return;
                }

                // email_list: need a token
                const token = accessToken || sessionStorage.getItem(tokenKey(slug));
                if (token) {
                    setViewState('viewing');
                } else {
                    setViewState('access_required');
                }
            })
            .catch(err => {
                if (err.response?.status === 404) {
                    // Not a document slug - it might be a shared folder instead
                    // (both resolve through this same /view/:slug route).
                    axios.get(`${API_BASE}/document-folders/by-slug/${slug}/access`)
                        .then(() => setFolderMode(true))
                        .catch(() => setViewState('not_found'));
                } else {
                    setErrorMsg('Failed to load document.');
                    setViewState('access_error');
                }
            });
    }, [slug, accessToken]); // eslint-disable-line react-hooks/exhaustive-deps

    // ─── Responsive PDF width ────────────────────────────────────────────────

    useEffect(() => {
        const update = () => {
            if (containerRef.current) {
                setPdfWidth(Math.min(containerRef.current.clientWidth - 32, 900));
            }
        };
        update();
        window.addEventListener('resize', update);
        return () => window.removeEventListener('resize', update);
    }, [viewState]);

    // ─── Tracking ────────────────────────────────────────────────────────────

    const endCurrentTrack = useCallback((endedAt?: string) => {
        const id = trackIdRef.current;
        if (!id || !slug) return;
        trackIdRef.current = null;

        const token = accessToken || sessionStorage.getItem(tokenKey(slug!));
        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        if (token) headers['Authorization'] = `Bearer ${token}`;

        fetch(`${API_BASE}/documents/${slug}/track/${id}`, {
            method: 'PATCH',
            headers,
            body: JSON.stringify({ sessionId: sessionId.current, endedAt: endedAt || new Date().toISOString() }),
            keepalive: true,
        }).catch(() => {});
    }, [slug, accessToken]);

    const startTrack = useCallback(async (page: number) => {
        if (!slug) return;
        const token = accessToken || sessionStorage.getItem(tokenKey(slug));
        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        if (token) headers['Authorization'] = `Bearer ${token}`;

        try {
            const { data } = await axios.post(
                `${API_BASE}/documents/${slug}/track`,
                { page: String(page), sessionId: sessionId.current, startedAt: new Date().toISOString() },
                { headers }
            );
            if (data.trackId) trackIdRef.current = data.trackId;
        } catch {
            // Tracking is best-effort — don't block the viewer
        }
    }, [slug, accessToken]);

    // Start tracking when viewing begins
    useEffect(() => {
        if (viewState !== 'viewing') return;
        startTrack(1);
    }, [viewState]); // eslint-disable-line react-hooks/exhaustive-deps

    // End track on unmount / tab close
    useEffect(() => {
        const handleUnload = () => endCurrentTrack();
        window.addEventListener('pagehide', handleUnload);
        return () => {
            window.removeEventListener('pagehide', handleUnload);
            endCurrentTrack();
        };
    }, [endCurrentTrack]);

    // ─── Page navigation + per-page tracking ────────────────────────────────

    const goToPage = useCallback(async (next: number) => {
        if (next < 1 || next > numPages) return;
        endCurrentTrack(new Date().toISOString());
        setCurrentPage(next);
        currentPageRef.current = next;
        await startTrack(next);
    }, [numPages, endCurrentTrack, startTrack]);

    // ─── Unlock a password-protected link ───────────────────────────────────

    const handleUnlock = async () => {
        if (!slug || !passwordInput) return;
        setUnlocking(true);
        setPasswordError('');
        try {
            const { data } = await axios.post(`${API_BASE}/documents/${slug}/unlock`, { password: passwordInput });
            setPasswordInput('');
            if (accessInfo?.accessType === 'anonymous') {
                // Public link: the unlock token is the access token the rest of the page sends.
                sessionStorage.setItem(tokenKey(slug), data.unlockToken);
                setAccessToken(data.unlockToken);
            } else {
                // Restricted link: keep the token for the verification request, then ask for the email.
                setUnlockToken(data.unlockToken);
                setViewState('access_required');
            }
        } catch (err: any) {
            setPasswordError(err.response?.data?.error || 'Incorrect password');
        } finally {
            setUnlocking(false);
        }
    };

    // ─── Request verification email ──────────────────────────────────────────

    const handleRequestAccess = async () => {
        if (!email.trim() || !slug) return;
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            setEmailError('Please enter a valid email address.');
            return;
        }
        setEmailError('');
        setRequestingAccess(true);
        try {
            await axios.post(`${API_BASE}/documents/${slug}/request-access`, { email: email.trim().toLowerCase(), unlockToken });
            setViewState('email_sent');
        } catch (err: any) {
            const msg = err.response?.data?.error || 'Failed to send verification email.';
            setEmailError(msg);
        } finally {
            setRequestingAccess(false);
        }
    };

    // ─── Stripe payment ───────────────────────────────────────────────────────

    const stripePromise = useMemo(() => {
        const key = accessInfo?.stripePublishableKey;
        return key ? loadStripe(key) : null;
    }, [accessInfo?.stripePublishableKey]);

    const handlePay = async () => {
        if (!slug) return;
        setPayLoading(true);
        setPayError('');
        const token = accessToken || sessionStorage.getItem(tokenKey(slug));
        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        if (token) headers['Authorization'] = `Bearer ${token}`;
        try {
            const { data } = await axios.post(`${API_BASE}/documents/${slug}/pay`, {}, { headers });
            setPayClientSecret(data.clientSecret);
            setPayModalOpen(true);
        } catch (err: any) {
            const errData = err.response?.data;
            if (errData?.alreadyPaid) {
                setPaySuccess(true);
                setPayModalOpen(true);
            } else {
                setPayError(errData?.error || 'Failed to initiate payment.');
            }
        } finally {
            setPayLoading(false);
        }
    };

    // ─── Compute PDF file prop ────────────────────────────────────────────────

    const pdfFile = React.useMemo(() => {
        if (!slug || viewState !== 'viewing' || accessInfo?.type !== 'pdf') return null;
        const token = accessToken || sessionStorage.getItem(tokenKey(slug));
        return {
            url: `${API_BASE}/documents/${slug}/file`,
            ...(token ? { httpHeaders: { Authorization: `Bearer ${token}` } } : {}),
        };
    }, [slug, viewState, accessInfo, accessToken]);

    // ─── Download ─────────────────────────────────────────────────────────────
    // A plain <a href> can't carry the Authorization header this access-
    // controlled file needs, so the PDF is fetched as a blob and handed to
    // the browser's own save-file flow via a temporary object URL.
    const [downloading, setDownloading] = useState(false);
    const handleDownload = async () => {
        if (!slug) return;
        setDownloading(true);
        const token = accessToken || sessionStorage.getItem(tokenKey(slug));
        const headers: Record<string, string> = {};
        if (token) headers['Authorization'] = `Bearer ${token}`;
        try {
            const { data } = await axios.get(`${API_BASE}/documents/${slug}/file`, {
                headers,
                params: { download: 'true' },
                responseType: 'blob',
            });
            const url = window.URL.createObjectURL(data);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${accessInfo?.title || 'document'}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch {
            // Access-controlled 403/404s are rare here (the viewer already
            // loaded the same file inline) - nothing useful to show beyond
            // just not downloading.
        } finally {
            setDownloading(false);
        }
    };

    // ─── Fetch report snapshot (webpage type) ───────────────────────────────

    useEffect(() => {
        if (!slug || viewState !== 'viewing' || accessInfo?.type !== 'webpage') return;
        const token = accessToken || sessionStorage.getItem(tokenKey(slug));
        const headers: Record<string, string> = {};
        if (token) headers['Authorization'] = `Bearer ${token}`;

        axios.get(`${API_BASE}/documents/${slug}/snapshot`, { headers })
            .then(({ data }) => {
                if (data.snapshotData) {
                    setSnapshot(data.snapshotData as ReportSnapshot);
                } else {
                    setSnapshotError('This shared report has no data.');
                }
            })
            .catch(() => setSnapshotError('Unable to load this report. Your access may have been revoked.'));
    }, [slug, viewState, accessInfo, accessToken]);

    // ─── Render states ────────────────────────────────────────────────────────

    if (folderMode && slug) {
        return <SharedFolderBrowser slug={slug} />;
    }

    if (viewState === 'loading') {
        return (
            <ViewerShell title="">
                <CenteredCard>
                    <CircularProgress sx={{ color: 'var(--color-primary)' }} />
                    <Typography sx={{ mt: 2, color: 'var(--color-text-muted)' }}>Loading document…</Typography>
                </CenteredCard>
            </ViewerShell>
        );
    }

    if (viewState === 'not_found') {
        return (
            <ViewerShell title="Not Found">
                <CenteredCard>
                    <FileText size={48} color="#ccc" />
                    <Typography variant="h6" sx={{ mt: 2, fontWeight: 600 }}>Document not found</Typography>
                    <Typography sx={{ color: 'var(--color-text-muted)', mt: 1 }}>This link may be invalid or the document has been removed.</Typography>
                </CenteredCard>
            </ViewerShell>
        );
    }

    if (viewState === 'expired') {
        return (
            <ViewerShell title="Link Expired">
                <CenteredCard>
                    <Clock size={48} color="#ccc" />
                    <Typography variant="h6" sx={{ mt: 2, fontWeight: 600 }}>This link has expired</Typography>
                    <Typography sx={{ color: 'var(--color-text-muted)', mt: 1 }}>The document owner has set an expiry date on this link.</Typography>
                </CenteredCard>
            </ViewerShell>
        );
    }

    if (viewState === 'access_error') {
        return (
            <ViewerShell title="Access Error">
                <CenteredCard>
                    <AlertCircle size={48} color="#e53935" />
                    <Typography variant="h6" sx={{ mt: 2, fontWeight: 600 }}>Access denied</Typography>
                    <Typography sx={{ color: 'var(--color-text-muted)', mt: 1, textAlign: 'center', maxWidth: 320 }}>{errorMsg}</Typography>
                    {accessInfo?.accessType === 'email_list' && (
                        <Button
                            variant="outlined"
                            sx={{ mt: 3, borderColor: 'var(--color-primary)', color: 'var(--color-primary)' }}
                            onClick={() => { setErrorMsg(''); setViewState('access_required'); }}
                        >
                            Try again
                        </Button>
                    )}
                </CenteredCard>
            </ViewerShell>
        );
    }

    if (viewState === 'password_required') {
        return (
            <ViewerShell title={accessInfo?.title || 'Protected Document'}>
                <CenteredCard>
                    <Lock size={48} color="var(--color-primary)" />
                    <Typography variant="h6" sx={{ mt: 2, fontWeight: 700 }}>
                        {accessInfo?.title || 'Protected Document'}
                    </Typography>
                    <Typography sx={{ color: 'var(--color-text-muted)', mt: 1, mb: 3, textAlign: 'center', maxWidth: 340 }}>
                        Enter the password to open this document.
                    </Typography>
                    <Box sx={{ width: '100%', maxWidth: 340 }}>
                        <TextField
                            fullWidth
                            size="small"
                            type="password"
                            placeholder="Password"
                            value={passwordInput}
                            onChange={e => { setPasswordInput(e.target.value); setPasswordError(''); }}
                            onKeyDown={e => e.key === 'Enter' && handleUnlock()}
                            error={!!passwordError}
                            helperText={passwordError}
                            sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        />
                        <Button
                            fullWidth
                            variant="contained"
                            onClick={handleUnlock}
                            disabled={unlocking || !passwordInput}
                            startIcon={unlocking ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : undefined}
                            sx={{ background: 'var(--color-primary)', borderRadius: 2, boxShadow: 'none', py: 1.2, '&:hover': { background: 'var(--color-primary-hover)' } }}
                        >
                            {unlocking ? 'Checking…' : 'Continue'}
                        </Button>
                    </Box>
                </CenteredCard>
            </ViewerShell>
        );
    }

    if (viewState === 'access_required') {
        return (
            <ViewerShell title={accessInfo?.title || 'Restricted Document'}>
                <CenteredCard>
                    <Lock size={48} color="var(--color-primary)" />
                    <Typography variant="h6" sx={{ mt: 2, fontWeight: 700 }}>
                        {accessInfo?.title || 'Restricted Document'}
                    </Typography>
                    <Typography sx={{ color: 'var(--color-text-muted)', mt: 1, mb: 3, textAlign: 'center', maxWidth: 340 }}>
                        This document is restricted. Enter the email address you were invited with and we'll send you a verification link.
                    </Typography>
                    <Box sx={{ width: '100%', maxWidth: 340 }}>
                        <TextField
                            fullWidth
                            size="small"
                            type="email"
                            placeholder="you@example.com"
                            value={email}
                            onChange={e => { setEmail(e.target.value); setEmailError(''); }}
                            onKeyDown={e => e.key === 'Enter' && handleRequestAccess()}
                            error={!!emailError}
                            helperText={emailError}
                            sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                        />
                        <Button
                            fullWidth
                            variant="contained"
                            onClick={handleRequestAccess}
                            disabled={requestingAccess || !email.trim()}
                            startIcon={requestingAccess ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : undefined}
                            sx={{ background: 'var(--color-primary)', borderRadius: 2, boxShadow: 'none', py: 1.2, '&:hover': { background: 'var(--color-primary-hover)' } }}
                        >
                            {requestingAccess ? 'Sending…' : 'Send verification link'}
                        </Button>
                    </Box>
                </CenteredCard>
            </ViewerShell>
        );
    }

    if (viewState === 'email_sent') {
        return (
            <ViewerShell title="Check your email">
                <CenteredCard>
                    <MailCheck size={48} color="var(--color-primary)" />
                    <Typography variant="h6" sx={{ mt: 2, fontWeight: 700 }}>Check your email</Typography>
                    <Typography sx={{ color: 'var(--color-text-muted)', mt: 1, textAlign: 'center', maxWidth: 340 }}>
                        We've sent a verification link to <strong>{email}</strong>. Click it to open the document.
                    </Typography>
                    <Button
                        variant="text"
                        sx={{ mt: 3, color: 'var(--color-primary)', textTransform: 'none' }}
                        onClick={() => setViewState('access_required')}
                    >
                        Use a different email
                    </Button>
                </CenteredCard>
            </ViewerShell>
        );
    }

    // ── viewing ──────────────────────────────────────────────────────────────
    const formattedAmount = accessInfo?.invoiceAmount != null && accessInfo?.invoiceCurrency
        ? `${accessInfo.invoiceCurrency.toUpperCase()} ${parseFloat(String(accessInfo.invoiceAmount)).toFixed(2)}`
        : null;

    return (
        <>
        <ViewerShell
            title={accessInfo?.title || ''}
            toolbar={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    {accessInfo?.type === 'pdf' && accessInfo?.allowDownloads && (
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={handleDownload}
                            disabled={downloading}
                            startIcon={downloading ? <CircularProgress size={14} /> : <Download size={15} />}
                            sx={{ borderColor: 'rgba(255,255,255,0.4)', color: '#fff', borderRadius: 2, fontSize: 13, textTransform: 'none', '&:hover': { borderColor: '#fff', background: 'rgba(255,255,255,0.08)' } }}
                        >
                            {downloading ? 'Downloading…' : 'Download'}
                        </Button>
                    )}
                    {accessInfo?.canPay && accessInfo?.stripePublishableKey && (
                        <Button
                            size="small"
                            variant="contained"
                            onClick={handlePay}
                            disabled={payLoading}
                            startIcon={payLoading
                                ? <CircularProgress size={14} sx={{ color: '#fff' }} />
                                : <CreditCard size={15} />
                            }
                            sx={{ background: 'var(--color-primary)', color: '#fff', borderRadius: 2, boxShadow: 'none', fontSize: 13, textTransform: 'none', '&:hover': { background: 'var(--color-primary-hover)' } }}
                        >
                            {payLoading ? 'Loading…' : formattedAmount ? `Pay ${formattedAmount}` : 'Pay Invoice'}
                        </Button>
                    )}
                    {accessInfo?.type === 'pdf' && numPages > 0 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Tooltip title="Previous page">
                                <span>
                                    <IconButton
                                        size="small"
                                        onClick={() => goToPage(currentPage - 1)}
                                        disabled={currentPage <= 1}
                                        sx={{ color: '#fff', '&:disabled': { color: 'rgba(255,255,255,0.3)' } }}
                                    >
                                        <ChevronLeft size={20} />
                                    </IconButton>
                                </span>
                            </Tooltip>
                            <Typography sx={{ color: '#fff', fontSize: 13, whiteSpace: 'nowrap', minWidth: 80, textAlign: 'center' }}>
                                Page {currentPage} of {numPages}
                            </Typography>
                            <Tooltip title="Next page">
                                <span>
                                    <IconButton
                                        size="small"
                                        onClick={() => goToPage(currentPage + 1)}
                                        disabled={currentPage >= numPages}
                                        sx={{ color: '#fff', '&:disabled': { color: 'rgba(255,255,255,0.3)' } }}
                                    >
                                        <ChevronRight size={20} />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Box>
                    )}
                </Box>
            }
        >
            {payError && (
                <Box sx={{ background: '#fff3f3', color: '#c00', px: 3, py: 1, fontSize: 13, borderBottom: '1px solid #fcc' }}>
                    {payError}
                </Box>
            )}
            <Box ref={containerRef} sx={{ width: '100%', display: 'flex', justifyContent: 'center', p: 2, overflowY: 'auto', flex: 1, '@media print': { overflow: 'visible' } }}>
                {accessInfo?.type === 'pdf' && pdfFile ? (
                    <Box sx={{ position: 'relative' }}>
                    <Document
                        file={pdfFile}
                        onLoadSuccess={({ numPages: n }) => setNumPages(n)}
                        onLoadError={() => {
                            setErrorMsg('Unable to load the document. Your access may have been revoked.');
                            setViewState('access_error');
                        }}
                        loading={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 8 }}>
                                <CircularProgress size={20} sx={{ color: 'var(--color-primary)' }} />
                                <Typography sx={{ color: 'var(--color-text-muted)' }}>Loading PDF…</Typography>
                            </Box>
                        }
                    >
                        <Page
                            pageNumber={currentPage}
                            width={pdfWidth}
                            renderTextLayer
                            renderAnnotationLayer
                            loading={null}
                        />
                    </Document>
                    </Box>
                ) : accessInfo?.type === 'webpage' ? (
                    snapshotError ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 8, gap: 1 }}>
                            <AlertCircle size={32} color="#e53935" />
                            <Typography sx={{ color: 'var(--color-text-muted)' }}>{snapshotError}</Typography>
                        </Box>
                    ) : snapshot ? (
                        <Box sx={{ width: '100%' }}>
                            <SharedReportView snapshot={snapshot} slug={slug!} accessToken={accessToken || sessionStorage.getItem(tokenKey(slug!))} title={accessInfo?.title || 'Report'} />
                        </Box>
                    ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 8 }}>
                            <CircularProgress size={20} sx={{ color: 'var(--color-primary)' }} />
                            <Typography sx={{ color: 'var(--color-text-muted)' }}>Loading report…</Typography>
                        </Box>
                    )
                ) : null}
            </Box>
        </ViewerShell>

        {/* Stripe Payment Modal */}
        {payModalOpen && payClientSecret && stripePromise && (
            <Dialog
                open={payModalOpen}
                onClose={() => { if (!paySuccess) { setPayModalOpen(false); setPayClientSecret(null); } }}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CreditCard size={18} color="var(--color-primary)" />
                        <span style={{ fontWeight: 700, fontSize: 18 }}>Pay Invoice</span>
                    </Box>
                    {!paySuccess && (
                        <IconButton size="small" onClick={() => { setPayModalOpen(false); setPayClientSecret(null); }}>
                            <X size={16} />
                        </IconButton>
                    )}
                </DialogTitle>
                <DialogContent>
                    {paySuccess ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 3 }}>
                            <CheckCircle size={48} color="var(--color-primary)" />
                            <Typography sx={{ mt: 2, fontWeight: 700, fontSize: 18 }}>Payment successful!</Typography>
                            <Typography sx={{ color: 'var(--color-text-muted)', mt: 1, textAlign: 'center' }}>
                                Your payment has been processed.
                            </Typography>
                            <Button
                                sx={{ mt: 3, background: 'var(--color-primary)', color: '#fff', borderRadius: 2, boxShadow: 'none', textTransform: 'none', '&:hover': { background: 'var(--color-primary-hover)' } }}
                                variant="contained"
                                onClick={() => { setPayModalOpen(false); setPayClientSecret(null); }}
                            >
                                Close
                            </Button>
                        </Box>
                    ) : (
                        <Elements stripe={stripePromise} options={{ clientSecret: payClientSecret, appearance: { theme: 'stripe' } }}>
                            <StripePayForm
                                amount={formattedAmount}
                                onSuccess={async (paymentIntentId) => {
                                    const token = accessToken || sessionStorage.getItem(tokenKey(slug!));
                                    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
                                    if (token) headers['Authorization'] = `Bearer ${token}`;
                                    try {
                                        await axios.post(
                                            `${API_BASE}/documents/${slug}/confirm-payment`,
                                            { paymentIntentId },
                                            { headers }
                                        );
                                    } catch {
                                        // Transaction recording failed — payment still went through,
                                        // so don't block the success state shown to the user
                                    }
                                    setPaySuccess(true);
                                }}
                            />
                        </Elements>
                    )}
                </DialogContent>
            </Dialog>
        )}
        </>
    );
};

// ─── Stripe payment form (must be inside <Elements>) ──────────────────────────

interface StripePayFormProps {
    amount: string | null;
    onSuccess: (paymentIntentId: string) => void;
}

const StripePayForm: React.FC<StripePayFormProps> = ({ amount, onSuccess }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [submitting, setSubmitting] = useState(false);
    const [formError, setFormError] = useState('');

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!stripe || !elements) return;
        setSubmitting(true);
        setFormError('');
        const { error, paymentIntent } = await stripe.confirmPayment({
            elements,
            confirmParams: { return_url: window.location.href },
            redirect: 'if_required',
        });
        if (error) {
            setFormError(error.message || 'Payment failed. Please try again.');
            setSubmitting(false);
        } else if (paymentIntent) {
            onSuccess(paymentIntent.id);
        }
    };

    return (
        <form onSubmit={handleSubmit} style={{ paddingBottom: 8 }}>
            {amount && (
                <Typography sx={{ fontWeight: 600, fontSize: 22, textAlign: 'center', mb: 2, color: '#1a1a2e' }}>
                    {amount}
                </Typography>
            )}
            <PaymentElement />
            {formError && (
                <Typography sx={{ color: '#e53935', fontSize: 13, mt: 1.5 }}>{formError}</Typography>
            )}
            <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={!stripe || !elements || submitting}
                startIcon={submitting ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <CreditCard size={16} />}
                sx={{ mt: 2.5, background: 'var(--color-primary)', color: '#fff', borderRadius: 2, boxShadow: 'none', textTransform: 'none', fontWeight: 600, py: 1.2, '&:hover': { background: 'var(--color-primary-hover)' } }}
            >
                {submitting ? 'Processing…' : 'Pay Now'}
            </Button>
        </form>
    );
};

export default DocumentViewer;
